import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { HeartHandshake, MessageCircle, Calendar, HeartCrack } from "lucide-react";
import { fireHeartsConfetti } from "@/lib/confetti";
import { useLocale } from "@/i18n/LocaleContext";

export default function RelationshipCard() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const { t, lang } = useLocale();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [endDialog, setEndDialog] = useState(false);

  const load = useCallback(() => {
    api.get("/relationship/status").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  if (!data || data.status === "none") return null;

  const partner = data.partner;
  const avatar = partner?.photos?.[0] || partner?.picture;

  const respond = async (accept) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/relationship/respond", { request_id: data.request_id, accept });
      if (accept) {
        fireHeartsConfetti();
        toast.success(t("rel.toastTogether").replace("{name}", partner?.name));
        checkAuth();
      } else {
        toast.success("OK");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/relationship/cancel");
      toast.success("Request cancelled");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const endRelationship = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/relationship/end");
      toast.success(t("rel.toastEnded"));
      setEndDialog(false);
      checkAuth();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass-card p-6 mb-6 border border-rose-500/25" data-testid="relationship-card">
      <div className="flex items-center gap-3 mb-3">
        <HeartHandshake className="w-5 h-5 text-rose-400" />
        <h3 className="text-lg font-semibold">{t("rel.title")}</h3>
      </div>

      {data.status === "active" && (
        <div data-testid="relationship-active">
          <div className="flex items-center gap-3 mb-4">
            {avatar && <img src={avatar} alt={partner?.name} className="w-12 h-12 rounded-full object-cover" />}
            <div>
              <p className="font-semibold">{t("rel.activeWith").replace("{name}", partner?.name || "")}</p>
              <p className="text-sm text-muted-foreground">
                {t("rel.activeSince").replace("{date}", data.since ? new Date(data.since).toLocaleDateString(lang) : "—")}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              className="bg-primary hover:bg-primary/90"
              onClick={() => navigate(`/profile/${partner?.user_id}?action=message`)}
              data-testid="relationship-chat-btn"
            >
              <MessageCircle className="w-4 h-4 mr-1.5" /> {t("rel.chat")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-white/10"
              onClick={() => navigate(`/profile/${partner?.user_id}?action=meetup`)}
              data-testid="relationship-meetup-btn"
            >
              <Calendar className="w-4 h-4 mr-1.5" /> {t("rel.planDate")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              onClick={() => setEndDialog(true)}
              data-testid="end-relationship-btn"
            >
              <HeartCrack className="w-4 h-4 mr-1.5" /> {t("rel.end")}
            </Button>
          </div>
        </div>
      )}

      {data.status === "incoming" && (
        <div data-testid="relationship-incoming">
          <div className="flex items-center gap-3 mb-4">
            {avatar && <img src={avatar} alt={partner?.name} className="w-12 h-12 rounded-full object-cover" />}
            <div>
              <p className="font-semibold">{t("rel.incomingTitle").replace("{name}", partner?.name || "")}</p>
              <p className="text-sm text-muted-foreground">
                {t("rel.incomingDesc")}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="bg-rose-500 hover:bg-rose-400 text-white"
              onClick={() => respond(true)}
              disabled={busy}
              data-testid="accept-relationship-btn"
            >
              {t("rel.accept")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-white/10"
              onClick={() => respond(false)}
              disabled={busy}
              data-testid="decline-relationship-btn"
            >
              {t("rel.decline")}
            </Button>
          </div>
        </div>
      )}

      {data.status === "outgoing" && (
        <div data-testid="relationship-outgoing">
          <div className="flex items-center gap-3 mb-4">
            {avatar && <img src={avatar} alt={partner?.name} className="w-12 h-12 rounded-full object-cover" />}
            <div>
              <p className="font-semibold">{t("rel.waitingFor").replace("{name}", partner?.name || "")}</p>
              <p className="text-sm text-muted-foreground">{t("rel.waitingDesc")}</p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-white/10"
            onClick={cancel}
            disabled={busy}
            data-testid="cancel-relationship-btn"
          >
            Cancel request
          </Button>
        </div>
      )}

      <Dialog open={endDialog} onOpenChange={setEndDialog}>
        <DialogContent className="max-w-sm bg-card border-white/10" data-testid="end-relationship-dialog">
          <DialogHeader>
            <DialogTitle>{t("rel.endTitle")}</DialogTitle>
            <DialogDescription>
              {t("rel.endDesc").replace("{name}", partner?.name || "")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end mt-2">
            <Button variant="outline" onClick={() => setEndDialog(false)} data-testid="cancel-end-btn">
              {t("rel.keepIt")}
            </Button>
            <Button
              className="bg-red-500 hover:bg-red-400 text-white"
              onClick={endRelationship}
              disabled={busy}
              data-testid="confirm-end-btn"
            >
              {t("rel.end")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
